"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import type { CmsSectionKey } from "@/lib/validations/cms";

export type EditTarget = {
  section: string;
  field: string;
  value: string;
  rect: DOMRect;
};

export type SectionEditTarget = {
  sectionKey: CmsSectionKey;
};

type HiddenSections = Record<string, boolean>;

type EditModeState = {
  isEditing: boolean;
  isAdmin: boolean;
  hiddenSections: HiddenSections;
  editTarget: EditTarget | null;
  sectionEditTarget: SectionEditTarget | null;
  toggleEditMode: () => void;
  toggleSectionVisibility: (sectionKey: string) => void;
  isSectionHidden: (sectionKey: string) => boolean;
  requestEdit: (target: EditTarget) => void;
  cancelEdit: () => void;
  openSectionEditor: (sectionKey: CmsSectionKey) => void;
  updateField: (section: string, field: string, value: unknown) => Promise<void>;
};

const EditModeContext = createContext<EditModeState | null>(null);

export function useEditMode() {
  const ctx = useContext(EditModeContext);
  if (!ctx) throw new Error("useEditMode must be used within EditModeProvider");
  return ctx;
}

export function EditModeProvider({ children }: { children: ReactNode }) {
  const [isEditing, setIsEditing] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hiddenSections, setHiddenSections] = useState<HiddenSections>({});
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [sectionEditTarget, setSectionEditTarget] = useState<SectionEditTarget | null>(null);

  useEffect(() => {
    fetch("/api/admin/edit-mode")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.isAdmin) setIsAdmin(true);
      })
      .catch(() => {});
  }, []);

  const toggleEditMode = useCallback(() => {
    setIsEditing((prev) => !prev);
    setEditTarget(null);
    setSectionEditTarget(null);
  }, []);

  const toggleSectionVisibility = useCallback((sectionKey: string) => {
    setHiddenSections((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  }, []);

  const isSectionHidden = useCallback(
    (sectionKey: string) => !!hiddenSections[sectionKey],
    [hiddenSections]
  );

  const requestEdit = useCallback((target: EditTarget) => {
    setEditTarget(target);
  }, []);

  const openSectionEditor = useCallback((sectionKey: CmsSectionKey) => {
    setSectionEditTarget({ sectionKey });
  }, []);

  const cancelEdit = useCallback(() => {
    setEditTarget(null);
    setSectionEditTarget(null);
  }, []);

  const updateField = useCallback(
    async (section: string, field: string, value: unknown): Promise<void> => {
      const result = await fetch("/api/admin/edit-mode", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ section, field, value }),
      });
      if (!result.ok) {
        const err = await result.json().catch(() => ({}));
        const msg = (err as { message?: string }).message ?? "Save failed";
        throw new Error(msg);
      }
    },
    []
  );

  const value = useMemo(
    () => ({
      isEditing,
      isAdmin,
      hiddenSections,
      editTarget,
      sectionEditTarget,
      toggleEditMode,
      toggleSectionVisibility,
      isSectionHidden,
      requestEdit,
      cancelEdit,
      openSectionEditor,
      updateField,
    }),
    [
      isEditing,
      isAdmin,
      hiddenSections,
      editTarget,
      sectionEditTarget,
      toggleEditMode,
      toggleSectionVisibility,
      isSectionHidden,
      requestEdit,
      cancelEdit,
      openSectionEditor,
      updateField,
    ]
  );

  return <EditModeContext.Provider value={value}>{children}</EditModeContext.Provider>;
}
